import { useMemo } from "react";
import {
  Award,
  BarChart2,
  CheckCircle,
  Clock,
  Crown,
  Hash,
  PlayCircle,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";

import BottomNav from "../components/BottomNav";
import { PosterSlot } from "../components/EntryVisuals";
import { TYPE_ORDER, TYPE_STYLES } from "../utils/constants";
import {
  averageRatingOutOfTen,
  formatEntryDate,
  summarize,
} from "../utils/helpers";
import { SHADOW, TOKENS as T } from "../styles/styles";

export default function StatsPage({
  activeGroup,
  groups = [],
  entries,
  currentUser,
  onNavigate,
}) {
  const overview = useMemo(() => summarize(entries), [entries]);

  // Only supported media types; types with no entries are dropped.
  const distribution = useMemo(() => {
    const rows = TYPE_ORDER.map((type) => ({
      type,
      label: TYPE_STYLES[type].label,
      color: TYPE_STYLES[type].color,
      Icon: TYPE_STYLES[type].icon,
      count: entries.filter((entry) => entry.type === type).length,
    })).filter((row) => row.count > 0);

    const max = rows.reduce((best, row) => Math.max(best, row.count), 0);

    return { rows, max };
  }, [entries]);

  const rating = useMemo(
    () => ({
      average: averageRatingOutOfTen(entries),
      count: entries.filter((entry) => Number(entry.rating) > 0).length,
    }),
    [entries],
  );

  const recent = useMemo(
    () =>
      [...entries]
        .sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
        )
        .slice(0, 5),
    [entries],
  );

  // activeGroup carries only id/name/inviteCode, so the full document is looked
  // up in the already-loaded groups list for member count and owner.
  const groupDoc = useMemo(
    () => groups.find((group) => group.id === activeGroup?.id) || null,
    [groups, activeGroup],
  );

  const memberCount =
    groupDoc?.memberCount || (groupDoc?.memberIds || []).length || 0;

  const ownerLabel = groupDoc?.ownerEmail
    ? groupDoc.ownerId === currentUser?.uid
      ? "You"
      : groupDoc.ownerEmail
    : "—";

  const overviewCards = [
    {
      label: "Total Entries",
      value: overview.total,
      Icon: TrendingUp,
      color: "#0077FF",
    },
    {
      label: "Completed",
      value: overview.completed,
      Icon: CheckCircle,
      color: "#059669",
    },
    {
      label: "In Progress",
      value: overview.inProgress,
      Icon: PlayCircle,
      color: "#7C3AED",
    },
    {
      label: "Planned",
      value: overview.planned,
      Icon: Clock,
      color: "#D97706",
    },
  ];

  return (
    <div style={pageStyle}>
      <div style={headerShellStyle}>
        <h1 style={screenTitleStyle}>Statistics</h1>
        <p style={screenSubtitleStyle}>
          {activeGroup?.name || "Personal"}
        </p>
      </div>

      <div style={listShellStyle} className="scrollbar-hide">
        {entries.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconBoxStyle}>
              <BarChart2 size={28} color={T.mutedForeground} />
            </div>
            <p style={emptyTitleStyle}>No stats yet</p>
            <p style={emptySubtitleStyle}>
              Track a few things and your numbers will build up here.
            </p>
          </div>
        ) : (
          <>
            {/* ── Section 1 — Overview ── */}
            <div style={overviewGridStyle}>
              {overviewCards.map(({ label, value, Icon, color }) => (
                <div key={label} style={statCardStyle}>
                  <div
                    style={{ ...statIconBoxStyle, background: `${color}15` }}
                  >
                    <Icon size={20} color={color} />
                  </div>
                  <p style={statValueStyle}>{value}</p>
                  <p style={statLabelStyle}>{label}</p>
                </div>
              ))}
            </div>

            {/* ── Section 3 — Average rating ── */}
            <div style={ratingCardStyle}>
              <div style={ratingIconBoxStyle}>
                <Star size={22} fill="#FBBF24" color="#FBBF24" />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={ratingLabelStyle}>Average Rating</p>
                <p style={ratingSubStyle}>
                  {rating.count > 0
                    ? `Across ${rating.count} rated ${
                        rating.count === 1 ? "entry" : "entries"
                      }`
                    : "No entries rated yet"}
                </p>
              </div>

              <div style={ratingValueWrapStyle}>
                <span style={ratingValueStyle}>
                  {rating.average === null ? "—" : rating.average.toFixed(1)}
                </span>
                <span style={ratingMaxStyle}>/10</span>
              </div>
            </div>

            {/* ── Section 2 — Media distribution ── */}
            <div style={panelStyle}>
              <h3 style={panelTitleStyle}>Media Distribution</h3>

              <div style={distributionListStyle}>
                {distribution.rows.map(({ type, label, color, Icon, count }) => (
                  <div key={type} style={distributionRowStyle}>
                    <div
                      style={{
                        ...distributionIconStyle,
                        background: `${color}18`,
                      }}
                    >
                      <Icon size={18} color={color} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={distributionHeaderStyle}>
                        <span style={distributionLabelStyle}>{label}</span>
                        <span style={distributionCountStyle}>{count}</span>
                      </div>

                      <div style={progressTrackStyle}>
                        <div
                          style={{
                            ...progressFillStyle,
                            width: `${(count / distribution.max) * 100}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section 4 — Recent activity ── */}
            <div style={panelStyle}>
              <h3 style={panelTitleStyle}>Recent Activity</h3>

              <div style={recentListStyle}>
                {recent.map((entry) => (
                  <div key={entry.id} style={recentRowStyle}>
                    <PosterSlot
                      type={entry.type}
                      width={44}
                      height={56}
                      radius={12}
                    />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={recentTitleStyle}>{entry.title}</p>
                      <p style={recentMetaStyle}>
                        {TYPE_STYLES[entry.type]?.label || "Unknown"}
                        {formatEntryDate(entry.createdAt) &&
                          ` · ${formatEntryDate(entry.createdAt)}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section 5 — Group summary ── */}
            <div style={panelStyle}>
              <h3 style={panelTitleStyle}>Group Summary</h3>

              <div style={summaryListStyle}>
                <div style={summaryRowStyle}>
                  <div style={summaryIconStyle}>
                    <Hash size={16} color={T.primary} />
                  </div>
                  <span style={summaryLabelStyle}>Current Group</span>
                  <span style={summaryValueStyle}>
                    {activeGroup?.name || "—"}
                  </span>
                </div>

                <div style={summaryRowStyle}>
                  <div style={summaryIconStyle}>
                    <Users size={16} color={T.primary} />
                  </div>
                  <span style={summaryLabelStyle}>Members</span>
                  <span style={summaryValueStyle}>
                    {memberCount || "—"}
                  </span>
                </div>

                <div style={summaryRowStyle}>
                  <div style={summaryIconStyle}>
                    <Award size={16} color={T.primary} />
                  </div>
                  <span style={summaryLabelStyle}>Total Entries</span>
                  <span style={summaryValueStyle}>{overview.total}</span>
                </div>

                <div style={summaryRowStyle}>
                  <div style={summaryIconStyle}>
                    <Crown size={16} color={T.primary} />
                  </div>
                  <span style={summaryLabelStyle}>Owner</span>
                  <span style={summaryValueStyle}>{ownerLabel}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav active="stats" onNavigate={onNavigate} />
    </div>
  );
}

const pageStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: T.background,
  color: T.foreground,
};

const headerShellStyle = {
  padding: "8px 20px 12px",
  flexShrink: 0,
};

const screenTitleStyle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.15,
  color: T.foreground,
};

const screenSubtitleStyle = {
  margin: "2px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
};

const listShellStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "8px 20px 150px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const overviewGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const statCardStyle = {
  background: T.card,
  borderRadius: 24,
  padding: 16,
  boxShadow: SHADOW.card,
};

const statIconBoxStyle = {
  width: 36,
  height: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const statValueStyle = {
  margin: 0,
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1,
  color: T.foreground,
};

const statLabelStyle = {
  margin: "6px 0 0",
  fontSize: 12,
  fontWeight: 600,
  color: T.mutedForeground,
};

const ratingCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  background: T.card,
  borderRadius: 24,
  padding: 20,
  boxShadow: SHADOW.card,
};

const ratingIconBoxStyle = {
  width: 48,
  height: 48,
  borderRadius: 16,
  background: "rgba(251,191,36,0.14)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const ratingLabelStyle = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: T.foreground,
};

const ratingSubStyle = {
  margin: "2px 0 0",
  fontSize: 12,
  color: T.mutedForeground,
};

const ratingValueWrapStyle = {
  display: "flex",
  alignItems: "baseline",
  gap: 2,
  flexShrink: 0,
};

const ratingValueStyle = {
  fontSize: 26,
  fontWeight: 800,
  color: T.foreground,
};

const ratingMaxStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: T.mutedForeground,
};

const panelStyle = {
  background: T.card,
  borderRadius: 24,
  padding: 20,
  boxShadow: SHADOW.card,
};

const panelTitleStyle = {
  margin: "0 0 16px",
  fontSize: 15,
  fontWeight: 700,
  color: T.foreground,
};

const distributionListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const distributionRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const distributionIconStyle = {
  width: 36,
  height: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const distributionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 6,
};

const distributionLabelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: T.foreground,
};

const distributionCountStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: T.foreground,
};

const progressTrackStyle = {
  height: 6,
  borderRadius: 999,
  background: T.muted,
  overflow: "hidden",
};

const progressFillStyle = {
  height: "100%",
  borderRadius: 999,
  transition: "width 0.6s ease",
};

const recentListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const recentRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const recentTitleStyle = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: T.foreground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const recentMetaStyle = {
  margin: "3px 0 0",
  fontSize: 12,
  color: T.mutedForeground,
};

const summaryListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const summaryRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  minHeight: 48,
};

const summaryIconStyle = {
  width: 32,
  height: 32,
  borderRadius: 10,
  background: "rgba(0,119,255,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const summaryLabelStyle = {
  flex: 1,
  fontSize: 13,
  fontWeight: 500,
  color: T.mutedForeground,
};

const summaryValueStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: T.foreground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "55%",
  textAlign: "right",
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "48px 24px",
  background: T.card,
  borderRadius: 24,
  boxShadow: SHADOW.card,
};

const emptyIconBoxStyle = {
  width: 64,
  height: 64,
  borderRadius: 24,
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const emptyTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: T.foreground,
};

const emptySubtitleStyle = {
  margin: "6px 0 0",
  fontSize: 13,
  lineHeight: 1.5,
  color: T.mutedForeground,
};
